/* =============================================================================
   Home Try On booking form
   -----------------------------------------------------------------------------
   Everything the previous version did is still here, unchanged in behaviour:
     - min date = today, pushed to tomorrow if the number already booked today
     - phone blur -> duplicate-booking lookup
     - pincode change -> serviceability check + city/state autofill
     - submit -> Google Apps Script, UTM/zoho params, fbq/gtag/dataLayer, redirect
   What is new:
     - per-field inline validation messages
     - null guards everywhere (works with the old markup too)
     - endpoints / messages / redirect can be overridden from the theme editor
     - flatpickr is optional (slot dropdown mode + graceful fallback)
     - tighter field rules: names reject digits/symbols, area/city must contain
       letters, pincode must not start with 0 (all format-only, no flow changes)
   ========================================================================== */

(function () {
  'use strict';

  var DEFAULTS = {
    endpoint:
      'https://script.google.com/macros/s/AKfycbw9z4I8E8ti210eXg4VXylvFhltjSPTBjJbLv5VF2q_aWyAiWP3JohngAkr-U0Um6naZg/exec',
    pincodeEndpoint:
      'https://script.google.com/macros/s/AKfycbyjnnlzPO_a0zt5CRDPGDAPe_3AHSbNMEXaj8DRWsXroXKTqA07oJspQ0zw5MkVRvGu/exec',
    redirect: '/pages/feedback-form',
    successMessage: 'Your Appointment has been successfully booked.',
    interestMessage:
      'Your interest has been received successfully. Soon we will offer the service on your location.'
  };

  var PATTERNS = {
    phone: /^[6-9][0-9]{9}$/,
    // Indian PIN codes never start with 0
    pincode: /^[1-9][0-9]{5}$/,
    digits: /[0-9]/
  };

  /* A person's name: one or more letters (any script), where separate words may
     be joined only by a run of space / . / ' / ’ / - . This rejects digits,
     symbols (@ # $ % ! …), emoji, and leading/trailing/standalone punctuation,
     while still accepting "O'Brien", "Jean-Paul", "Dr. Smith", "D’Souza".
     Built through the constructor so we can fall back where \p{L} is unsupported. */
  try {
    PATTERNS.name = new RegExp("^\\p{L}+(?:[\\s.'\u2019-]+\\p{L}+)*$", 'u');
    PATTERNS.letter = new RegExp('\\p{L}', 'u');
    // Any character that has no business being in an address: @ $ % ^ * = _ ~ etc.
    // Allowed: letters, marks, digits, whitespace and , . ' " / # & ( ) - : ;
    PATTERNS.addressJunk = new RegExp("[^\\p{L}\\p{M}0-9\\s,.'\"/#&()\\-:;]", 'u');
  } catch (e) {
    PATTERNS.name = /^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[\s.'\u2019-]+[A-Za-zÀ-ÖØ-öø-ÿ]+)*$/;
    PATTERNS.letter = /[A-Za-zÀ-ÖØ-öø-ÿ]/;
    PATTERNS.addressJunk = /[^A-Za-zÀ-ÖØ-öø-ÿ0-9\s,.'"\/#&()\-:;]/;
  }

  function todayISO() {
    var d = new Date();
    var offset = d.getTimezoneOffset();
    return new Date(d.getTime() - offset * 60000).toISOString().split('T')[0];
  }

  function tomorrowISO() {
    var d = new Date();
    d.setDate(d.getDate() + 1);

    var offset = d.getTimezoneOffset();
    return new Date(d.getTime() - offset * 60000)
      .toISOString()
      .split('T')[0];
  }

  function setStateValue(stateInput, stateName) {
    if (!stateInput) return;

    if (stateInput.tagName === 'SELECT') {
      // Drop any option we added for a previous lookup before re-checking
      Array.prototype.slice
        .call(stateInput.querySelectorAll('option[data-auto-added]'))
        .forEach(function (opt) { opt.remove(); });

      if (stateName) {
        var matched = Array.prototype.some.call(stateInput.options, function (opt) {
          return opt.value === stateName;
        });

        if (!matched) {
          var opt = document.createElement('option');
          opt.value = stateName;
          opt.textContent = stateName;
          opt.dataset.autoAdded = 'true';
          stateInput.appendChild(opt);
        }
      }
    }

    stateInput.value = stateName || '';
  }

  function deriveLocality(postOffice) {
    if (!postOffice) return '';
    // Prefer the real town/block over the postal Division. The Division is postal
    // geography, not the city — e.g. 201301's Division is "Ghaziabad" but the town
    // is "Noida". Block/District/Name all resolve to the actual place.
    var candidate = postOffice.Block || postOffice.District || postOffice.Name || '';
    // Trim PO sub-locality noise: "Noida Sector 12" -> "Noida".
    return candidate.replace(/\s*(sector|phase|block)\b.*$/i, '').trim();
  }

  class CustomForm extends HTMLElement {
    constructor() {
      super();

      this.serviceErrorMsg = this.querySelector('#service-message');
      this.isServiceable = false;

      this.htoGoogleSheetUrl = this.dataset.endpoint || DEFAULTS.endpoint;
      this.pincodeSheetUrl = this.dataset.pincodeEndpoint || DEFAULTS.pincodeEndpoint;
      this.redirectUrl = this.dataset.redirect || DEFAULTS.redirect;
      this.successMessageText = this.dataset.successMessage || DEFAULTS.successMessage;
      this.interestMessageText = this.dataset.interestMessage || DEFAULTS.interestMessage;
      this.requirePincode = this.dataset.requirePincode !== 'false';
      this.allowNonServiceable = this.dataset.allowNonServiceable === 'true';

      this.fields = [];
      this.pincodeBound = false;
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      if (!this.serviceErrorMsg) this.serviceErrorMsg = this.querySelector('#service-message');
      this.submitButton = this.querySelector('#submit-button') || document.querySelector('#submit-button');

      var today = todayISO();
      var dateField = this.querySelector('input[name="Appointment Date"]');
      if (dateField) dateField.setAttribute('min', today);

      var phoneNumberInput = this.querySelector('input[name="Phone Number"]');
      if (phoneNumberInput) {
        phoneNumberInput.addEventListener('input', function () {
          if (this.value.length > 14) this.value = this.value.slice(0, 14);
        });

        phoneNumberInput.addEventListener('blur', () => {
          if (phoneNumberInput.value.length === 10) {
            this.isUserRegisteredOnThisDay(phoneNumberInput.value);
            // Immediately enable date field for all users
            var dateInput = this.querySelector('custom-date-picker input');
            if (dateField) dateField.setAttribute('min', today);
            if (dateInput) dateInput.readOnly = false;
          }
        });
      }

      this.pincode();
      this.bindValidation();

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    /* --------------------------- validation layer -------------------------- */

    bindValidation() {
      this.fields = Array.prototype.slice
        .call(this.querySelectorAll('[data-validate]'))
        .filter(function (field) {
          // Skip hidden inputs (e.g. an optional field turned off in the editor)
          return field.type !== 'hidden';
        });

      this.fields.forEach((field) => {
        field.addEventListener('blur', () => this.showFieldError(field));
        field.addEventListener('change', () => this.showFieldError(field));
        field.addEventListener('input', () => {
          if (field.classList.contains('has-error')) this.showFieldError(field);
        });
      });
    }

    errorSlotFor(field) {
      var wrap = field.closest('.hto-field');
      if (wrap) {
        var slot = wrap.querySelector('[data-error-slot]');
        if (slot) return slot;
      }
      return null;
    }

    setError(field, message) {
      var slot = this.errorSlotFor(field);
      var hasError = !!message;

      field.classList.toggle('has-error', hasError);
      field.setAttribute('aria-invalid', hasError ? 'true' : 'false');

      if (!slot) return;

      slot.textContent = message || '';

      if (slot.id === 'service-message') {
        // shared with the serviceability messaging
        slot.classList.remove('validation-in-progress', 'validation-success');
        slot.classList.toggle('validation-failure', hasError);
        slot.classList.toggle('visibility-hidden', !hasError);
      }
    }

    getMessage(field) {
      var type = field.dataset.validate;
      var value = (field.value == null ? '' : String(field.value)).trim();
      var required = field.hasAttribute('required');
      var requiredMsg = field.dataset.errorRequired || 'This field is required';
      var formatMsg = field.dataset.errorFormat || 'Please check this field';

      if (!value) return required ? requiredMsg : '';

      switch (type) {
        case 'name':
          // must be at least 2 chars AND only letters + simple separators
          if (value.length < 2 || !PATTERNS.name.test(value)) return formatMsg;
          return '';
        case 'phone':
          if (!PATTERNS.phone.test(value)) return formatMsg;
          return '';
        case 'pincode':
          if (!PATTERNS.pincode.test(value)) return formatMsg;
          return '';
        case 'date': {
          var min = field.getAttribute('min');
          if (min && value < min) return formatMsg;
          return '';
        }
        case 'address':
          // 1) long enough  2) has real words  3) no junk symbols (@ $ % * …)
          // 4) has some structure — a real address carries a space or a house number
          if (value.length < 10 || !PATTERNS.letter.test(value)) return formatMsg;
          if (PATTERNS.addressJunk.test(value)) return formatMsg;
          if (!/\s/.test(value) && !PATTERNS.digits.test(value)) return formatMsg;
          return '';
        case 'text':
          // location/area & city: must have at least one letter (blocks "123456", "@@@")
          // and must not contain junk symbols (@ $ % * = _ ~ …)
          if (value.length < 2 || !PATTERNS.letter.test(value))
            return field.dataset.errorFormat || requiredMsg;
          if (PATTERNS.addressJunk.test(value))
            return field.dataset.errorFormat || requiredMsg;
          return '';
        default:
          return '';
      }
    }

    showFieldError(field) {
      var message = this.getMessage(field);

      // The pincode slot is also used by the serviceability check — only write to
      // it when there is a real problem, otherwise leave that message in place.
      if (field.id === 'home-try-on-pincode' && !message) {
        field.classList.remove('has-error');
        field.setAttribute('aria-invalid', 'false');
        return '';
      }

      this.setError(field, message);
      return message;
    }

    clearAllErrors() {
      this.fields.forEach((field) => this.setError(field, ''));
    }

    validateAll() {
      var firstInvalid = null;

      this.fields.forEach((field) => {
        var message = this.showFieldError(field);
        if (message && !firstInvalid) firstInvalid = field;
      });

      if (!firstInvalid && !this.isServiceable && !this.allowNonServiceable && this.requirePincode) {
        var pin = this.querySelector('#home-try-on-pincode');
        if (pin) {
          this.setError(pin, pin.dataset.errorService || 'Please enter a serviceable pincode');
          firstInvalid = pin;
        }
      }

      if (firstInvalid) {
        try {
          firstInvalid.focus({ preventScroll: true });
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          firstInvalid.focus();
        }
        return false;
      }

      return true;
    }

    /* ------------------------------- submit -------------------------------- */

    onSubmitHandler(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      var honeypot = this.querySelector('[data-honeypot]');
      if (honeypot && honeypot.value) return;

      if (!this.validateAll()) return;

      var form = this.querySelector('form');
      var formData = new FormData(form);
      var nitroIdentity = {
        name: String(formData.get('Name') || '').trim(),
        phone: String(formData.get('Phone Number') || '').trim(),
        email: String(formData.get('Email') || formData.get('email') || '').trim()
      };
      var nitroDetails = {
        name: nitroIdentity.name,
        phone: nitroIdentity.phone,
        email: nitroIdentity.email,
        pincode: String(formData.get('Pincode') || '').trim(),
        city: String(formData.get('City') || '').trim(),
        state: String(formData.get('State') || '').trim(),
        appointment_date: String(formData.get('Appointment Date') || '').trim(),
        appointment_time: String(formData.get('Time') || '').trim()
      };
      var submitButton = this.submitButton || document.querySelector('#submit-button');
      var successMessage = this.querySelector('#form-message');
      var dateInput = this.querySelector('custom-date-picker input');
      if (dateInput) dateInput.readOnly = true;

      var WEBHOOK_URL = this.htoGoogleSheetUrl;

      var utmSource = '';
      var zohoData = null;
      try {
        utmSource = sessionStorage.getItem('utmData') || '';
        zohoData = JSON.parse(sessionStorage.getItem('zohoUtmData'));
      } catch (e) {
        utmSource = '';
        zohoData = null;
      }

      formData.append('Source', utmSource);

      if (zohoData) {
        formData.append('UTM Adset', zohoData.utm_adset || '');
        formData.append('UTM Ad', zohoData.utm_ad || '');
        formData.append('UTM Campaign', zohoData.utm_campaign || '');
        formData.append('GCLID', zohoData.gclid || '');
        formData.append('FBCLID', zohoData.fbclid || '');
      }

      this.isSubmitting = true;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('loading');
      }
      if (successMessage) {
        successMessage.textContent = '';
        successMessage.style.color = '';
      }

      fetch(WEBHOOK_URL, {
        method: 'POST',
        body: formData
      })
        .then((response) => {
          if (response.ok) {
            form.reset();
            this.clearAllErrors();

            if (successMessage) {
              successMessage.textContent = this.isServiceable
                ? this.successMessageText
                : this.interestMessageText;
              successMessage.style.color = 'green';
            }

            // Fire Nitro tracking on form submission
            var nitroPayload = {
              form: 'home_try_on',
              status: 'submitted',
              pincode: nitroDetails.pincode,
              city: nitroDetails.city,
              state: nitroDetails.state,
              appointment_date: nitroDetails.appointment_date,
              appointment_time: nitroDetails.appointment_time,
              serviceable: this.isServiceable,
              page: window.location.href,
              source: 'form_submit'
            };

            var nitroResult = null;
            if (typeof window.nitroIdentifyAndTrack === 'function') {
              nitroResult = window.nitroIdentifyAndTrack(nitroIdentity, nitroPayload);
            } else if (typeof window.nitro === 'function') {
              try {
                var safeEmail = nitroIdentity.email || null;
                var safePhone = nitroIdentity.phone || null;
                var safeName = nitroIdentity.name || null;
                if (typeof window.nitro.identify === 'function') {
                  window.nitro.identify(safeEmail, safePhone, safeName);
                } else {
                  window.nitro('identify', safeEmail, safePhone, safeName);
                }
                if (typeof window.nitro.track === 'function') {
                  window.nitro.track('hto_form_submission', nitroPayload);
                } else {
                  window.nitro('track', 'hto_form_submission', nitroPayload);
                }
                nitroResult = { success: true };
              } catch (e) {
                console.error('Nitro tracking on submit failed:', e);
              }
            }

            if (nitroResult && nitroResult.success) {
              nitroDetails.firedOnSubmit = true;
            }

            if (this.isServiceable) {
              try {
                sessionStorage.setItem('htoSubmitted', 'true');
                sessionStorage.setItem('htoNitroSubmission', JSON.stringify(nitroDetails));
              } catch (e) {
                /* storage blocked — not critical */
              }

              if (typeof fbq === 'function') fbq('track', 'Lead');

              setTimeout(() => {
                window.location.href = this.redirectUrl;
              }, 2000);
            }

            if (window.dataLayer && typeof window.dataLayer.push === 'function') {
              window.dataLayer.push({ event: 'HTO_Booking' });
            }

            this.gtagReportConversion();
          } else {
            if (successMessage) {
              successMessage.textContent = 'There was an error saving your data, please try again';
              successMessage.style.color = 'red';
            }
          }
        })
        .catch((error) => {
          console.error('Data Saving Error:', error);
          if (successMessage) {
            successMessage.textContent = 'There was an error saving your data, please try again';
            successMessage.style.color = 'red';
          }
        })
        .finally(() => {
          this.isSubmitting = false;
          if (submitButton) {
            submitButton.classList.remove('loading');
            if (!this.requirePincode) submitButton.disabled = false;
          }
          if (this.serviceErrorMsg) {
            this.serviceErrorMsg.textContent = '';
            this.serviceErrorMsg.classList.remove(
              'validation-in-progress',
              'validation-success',
              'validation-failure'
            );
            this.serviceErrorMsg.classList.add('visibility-hidden');
          }
        });
    }

    gtagReportConversion() {
      if (typeof gtag !== 'function') return;
      try {
        gtag('event', 'conversion', {
          send_to: 'AW-386560685/ITZ2CO6A9KQZEK3lqbgB'
        });
      } catch (e) {
        /* tag manager not ready — ignore */
      }
    }

    /* ---------------------------- serviceability --------------------------- */

    validatePincode = async (zipcode) => {
      var pageHandle = this.dataset.page;
      var submitButton = this.submitButton || this.querySelector('#submit-button');
      var pincodeSheetUrl = this.pincodeSheetUrl;

      // Tuning knobs — safe to adjust:
      var TIMEOUT_MS = 8000;   // hard cap per attempt
      var MAX_ATTEMPTS = 3;    // 1 try + 2 retries
      var BACKOFF_MS = 800;    // wait between attempts

      if (submitButton && this.requirePincode) submitButton.disabled = true;
      this.isServiceable = false;

      if (!this.serviceErrorMsg || !zipcode) return;
      if (!PATTERNS.pincode.test(zipcode)) return;

      // Remember which pincode this run is for, so a slow/stale response can't
      // override the result of a newer one the user typed in the meantime.
      this._activePincode = zipcode;

      this.serviceErrorMsg.textContent = `Please wait! we are validating the ${zipcode} pincode...`;
      this.serviceErrorMsg.classList.remove('visibility-hidden', 'validation-failure', 'validation-success');
      this.serviceErrorMsg.classList.add('validation-in-progress');

      var attemptFetch = async function () {
        var controller = new AbortController();
        var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
        try {
          var response = await fetch(pincodeSheetUrl, {
            method: 'POST',
            body: new URLSearchParams({ pincode: zipcode, sheet: pageHandle }),
            signal: controller.signal
          });
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return await response.json();
        } finally {
          clearTimeout(timer);
        }
      };

      var data = null;
      var lastError = null;
      for (var attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          data = await attemptFetch();
          break;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS - 1) {
            await new Promise(function (r) { setTimeout(r, BACKOFF_MS); });
          }
        }
      }

      // If the user has since typed a different pincode, this result is stale — drop it.
      if (this._activePincode !== zipcode) return;

      if (!data) {
        // Every attempt timed out or errored.
        console.error('Error validating pincode:', lastError);
        this.serviceErrorMsg.textContent =
          `Couldn't verify ${zipcode} right now. Please re-enter the pincode to try again.`;
        this.serviceErrorMsg.classList.remove('validation-in-progress', 'validation-success');
        this.serviceErrorMsg.classList.add('validation-failure');
        // Honour existing config: keep disabled only when a serviceable pincode is required.
        if (submitButton) {
          submitButton.disabled = this.requirePincode && !this.allowNonServiceable;
        }
        return;
      }

      if (data.serviceable) {
        this.isServiceable = true;
        this.serviceErrorMsg.textContent = `We are happy that, ${zipcode} pincode is serviceable`;
        this.serviceErrorMsg.classList.remove('validation-in-progress', 'validation-failure');
        this.serviceErrorMsg.classList.add('validation-success');
        if (submitButton) submitButton.disabled = false;
      } else {
        this.isServiceable = false;
        this.serviceErrorMsg.textContent = `*Sorry! ${zipcode} pincode is not serviceable.`;
        this.serviceErrorMsg.classList.remove('validation-in-progress', 'validation-success');
        this.serviceErrorMsg.classList.add('validation-failure');
        if (submitButton && this.requirePincode && !this.allowNonServiceable) {
          submitButton.disabled = true;
        } else if (submitButton && this.allowNonServiceable) {
          submitButton.disabled = false;
        }
      }
    };

    isUserRegisteredOnThisDay = async (phone) => {
      var WEBHOOK_URL = `${this.htoGoogleSheetUrl}?phone=${phone}`;
      var dateInput = this.querySelector('custom-date-picker input');
      var dateField = this.querySelector('input[name="Appointment Date"]');
      var today = new Date();
      var todayFormatted = todayISO();
      var tomorrowFormatted = tomorrowISO();
      if (dateInput) dateInput.readOnly = true;

      try {
        var response = await fetch(WEBHOOK_URL);
        var data = await response.json();
        var appointmentDateObj = new Date(data.appointmentDate);
        var formattedAppointmentDate = appointmentDateObj.toISOString().split('T')[0];

      if (dateField) {
        dateField.setAttribute('min', todayFormatted);
      }
      } catch (error) {
        if (dateField) dateField.setAttribute('min', todayFormatted);
      } finally {
        if (dateInput) dateInput.readOnly = false;
        if (dateField) this.showFieldError(dateField);
      }
    };

    pincode() {
      if (this.pincodeBound) return;

      var pincodeInput = this.querySelector('#home-try-on-pincode');
      if (!pincodeInput) return;
      this.pincodeBound = true;

      var cityInput =
        this.querySelector('#home-try-on-city') || document.querySelector('#home-try-on-city');

      // Once the user starts typing the city themselves, drop the manual-entry note.
      if (cityInput) {
        cityInput.addEventListener('input', () => {
          var note = cityInput.parentNode && cityInput.closest('.hto-field')
            ? cityInput.closest('.hto-field').querySelector('[data-city-note]')
            : null;
          if (note && cityInput.value.trim()) note.style.display = 'none';
        });
      }

      pincodeInput.addEventListener('change', async (event) => {
        var zip = event.target.value;

        if (!PATTERNS.pincode.test(zip)) {
          this.showFieldError(pincodeInput);
          return;
        }

        // Serviceability check (sheet-driven) — unchanged.
        this.validatePincode(zip);

        var stateInput =
          this.querySelector('#home-try-on-state') || document.querySelector('#home-try-on-state');

        try {
          var response = await fetch(`https://api.postalpincode.in/pincode/${zip}`);
          var data = await response.json();
          var offices = (data && data[0] && data[0].PostOffice) || [];

          if (data[0].Status === 'Success' && offices.length) {
            var postOfficeData = offices[0];
            var city = deriveLocality(postOfficeData);

            if (city && cityInput) {
              cityInput.value = city;
              cityInput.readOnly = false;
              this.setError(cityInput, '');
              var okNote = this.cityNoteFor(cityInput);
              if (okNote) okNote.style.display = 'none';   // hide any leftover note
            } else {
              // Record exists but no usable city name — fall back to manual entry.
              this.enableManualCity(cityInput);
            }
            setStateValue(stateInput, postOfficeData.State || '');
          } else {
            // Status "Error" — the postal API has no record for this pincode.
            this.enableManualCity(cityInput);
            setStateValue(stateInput, '');
          }
        } catch (error) {
          console.error('Postal API error:', error);
          this.enableManualCity(
            cityInput,
            "Couldn't reach the location service — please enter your city."
          );
        }
      });
    }

    // Finds (or lazily creates) a small note element under the city input.
    cityNoteFor(cityInput) {
      if (!cityInput) return null;
      var wrap = cityInput.closest('.hto-field') || cityInput.parentNode;
      if (!wrap) return null;

      var note = wrap.querySelector('[data-city-note]');
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-city-note', '');
        note.setAttribute('aria-live', 'polite');
        note.className = 'hto-city-note';
        // Inline defaults so it works without any CSS change; override via the class.
        note.style.margin = '4px 0 0';
        note.style.fontSize = '11.5px';
        note.style.color = '#b45309';        // amber-700 — soft warning, still readable
        wrap.appendChild(note);
      }
      return note;
    }

    // Postal API had nothing usable — let the user type the city, and tell them why.
    enableManualCity(cityInput, message) {
      if (!cityInput) return;
      cityInput.value = '';
      cityInput.readOnly = false;
      cityInput.removeAttribute('readonly');
      this.setError(cityInput, '');

      var note = this.cityNoteFor(cityInput);
      if (note) {
        note.textContent =
          message || "We couldn't fetch the city for this pincode — please enter it manually.";
        note.style.display = '';
      }
    }
  }

  class CustomDatePicker extends HTMLElement {
    constructor() {
      super();
      this.dateInput = this.querySelector('input');
    }

    connectedCallback() {
      if (!this.dateInput) this.dateInput = this.querySelector('input');
      if (!this.dateInput) return;

      this.dateInput.addEventListener('click', () => {
        if (typeof this.dateInput.showPicker === 'function') {
          try {
            this.dateInput.showPicker();
          } catch (e) {
            this.dateInput.focus();
          }
        } else {
          this.dateInput.focus();
        }
      });
    }
  }

  class CustomTimePicker extends HTMLElement {
    constructor() {
      super();
      this.timeInput = this.querySelector('input, select');
    }

    connectedCallback() {
      if (!this.timeInput) this.timeInput = this.querySelector('input, select');
      if (!this.timeInput) return;

      // Slot dropdown mode needs no picker library
      if (this.timeInput.tagName === 'SELECT' || this.dataset.mode === 'select') return;

      var options = {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'h:i K',
        time_24hr: false,
        minTime: this.dataset.minTime || '09:00',
        maxTime: this.dataset.maxTime || '19:00'
      };

      if (typeof flatpickr === 'function') {
        flatpickr(this.timeInput, options);
        return;
      }

      // CDN still loading — poll briefly, then fall back to the native time input
      var attempts = 0;
      var timer = setInterval(() => {
        attempts += 1;
        if (typeof flatpickr === 'function') {
          clearInterval(timer);
          flatpickr(this.timeInput, options);
        } else if (attempts > 50) {
          clearInterval(timer);
          this.timeInput.type = 'time';
        }
      }, 100);
    }
  }

  if (!customElements.get('custom-form')) customElements.define('custom-form', CustomForm);
  if (!customElements.get('custom-date-picker')) customElements.define('custom-date-picker', CustomDatePicker);
  if (!customElements.get('custom-time-picker')) customElements.define('custom-time-picker', CustomTimePicker);
})();
